import React, { useEffect, useState, useRef, type SyntheticEvent, useMemo } from 'react';
import Verify from './Verify';
import TelInput from './TelInput';
import Loading from './Loading';
import { FileUploader } from './FileUploader';
import { type CustomField, CustomFieldType } from './interfaces';
import { parentMessage } from '../common/utils';
import TurnstileWidget from './TurnstileWidget';
import { waitForCondition, waitForTurnstile } from '../common/utils';
import { saveAuthData, clearAuthData } from '../common/authStorage';
import {
  getEnabledSocialProviders,
  getSocialErrorTypes,
  getSocialSuccessTypes,
  type SocialProviderConfig,
} from '../constants/socialProviders';

interface LoginProps {
  isEmailAllowed: boolean;
  isMobileAllowed: boolean;
  isEmailRequired: boolean;
  supportWebAuth: boolean;
  withoutReload: boolean;
  salla: any;
  inline: boolean;
  hideHeader?: boolean;
  outlineButtonNoBorder?: boolean;
}
interface VerifyTabRef {
  open: (data: any) => void;
  getCode: () => Promise<string>;
}
interface TelInputRef {
  getValues: () => Promise<{ [key: string]: string }>;
  isValid: () => Promise<boolean>;
}

const Login: React.FC<LoginProps> = ({
  isEmailAllowed,
  isMobileAllowed,
  isEmailRequired,
  supportWebAuth,
  withoutReload,
  salla,
  inline,
  hideHeader = false,
  outlineButtonNoBorder = false,
}) => {
  const [currentTabName, setCurrentTabName] = useState('home');
  // eslint-disable-next-line
  const [_, setPhone] = useState({ number: '', country_code: '' });

  const [showVerify, setShowVerify] = useState(false);
  const [emailErrorMsg] = useState(salla.lang.get('common.elements.email_is_valid'));
  // const [mobileErrorMsg] = useState(salla.lang.get('mobile_app.strings.incorrect_mobile'));
  const [errorMsg] = useState(salla.lang.get('common.errors.error_occurred'));
  const [firstNameErrorMsg] = useState(salla.lang.get('common.errors.field_required', { attribute: salla.lang.get('pages.profile.first_name') }));
  const [lastNameErrorMsg] = useState(salla.lang.get('common.errors.field_required', { attribute: salla.lang.get('pages.profile.last_name') }));
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Get social providers with salla config
  const enabledSocialProviders = getEnabledSocialProviders(salla);
  const socialErrorTypes = getSocialErrorTypes(salla);
  const socialSuccessTypes = getSocialSuccessTypes(salla);
  
  // Refs
  const mobileTab = useRef(null);
  const emailTab = useRef(null);
  const verifyTab = useRef(null);
  const verifyRef = useRef<VerifyTabRef | null>(null);
  const loginEmail = useRef<HTMLInputElement>(null);
  const smsBtn = useRef(null);
  const emailBtn = useRef(null);
  const loginTelInput = useRef<TelInputRef | null>(null);

  // Register vars
  const [regType, setRegType] = useState('phone');
  const [customFieldsValues, setCustomFieldsValues] = useState<{ [key: string]: string }>({});
  const [socialData, setSocialData] = useState<{ provider_id?: string; provider?: string; code?: string; social_registration_token?: string | null }>({});

  const customFieldsWrapperRef = useRef<HTMLDivElement | null>(null);
  // Register Refs
  const regBtn = useRef(null);
  const firstName = useRef<HTMLInputElement>(null);
  const lastName = useRef<HTMLInputElement>(null);
  const regTelInput = useRef<TelInputRef | null>(null);
  const registrationTab = useRef(null);
  const regEmail = useRef<HTMLInputElement>(null);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [agreeToMarketing, setAgreeToMarketing] = useState(true);

  const urls = useMemo(() => {
    const baseUrl = Salla.config.get('store.url');
    return {
      privacyPolicy: `${baseUrl}/privacy-policy`,
    }
  }, [])

  function showGuestCheckoutLink() {
    const urlParams = new URLSearchParams(window.location.search);
    const sourceValue = urlParams.get('src');
    return sourceValue === 'cart-submit' && Salla.config.get('store').features.includes('guest-checkout');
  }

  function generateTabClasses(tabName: string) {
    const classes = {
      's-login-modal-tab': tabName !== 'registration' && tabName !== 'otp',
      "s-hidden": currentTabName !== tabName,
      "s-show": currentTabName === tabName,
      's-login-modal-unactive': tabName !== 'registration' && tabName !== 'otp' && currentTabName !== tabName,
      's-login-modal-active': tabName !== 'registration' && tabName !== 'otp' && currentTabName === tabName
    }

    return Object.entries(classes)
      .filter(([className, shouldApply]) => shouldApply)
      .map(([className]) => className)
      .join(' ');
  }

  function generateRegClasses(type: string) {
    const classes = {
      "mb-1.5": true,
      "s-hidden": regType === type
    }
    return Object.entries(classes)
      .filter(([_, shouldApply]) => shouldApply)
      .map(([className]) => className)
      .join(' ');
  }

  function typing(e: React.KeyboardEvent<HTMLInputElement>, submitMethod: (() => void) | null = null) {
    const error = (e.target as HTMLInputElement).nextElementSibling;
    (e.target as HTMLElement).classList.remove('s-has-error');
    if (error?.classList.contains('s-login-modal-error-message')) {
      ((error as HTMLElement).innerText = '');
    }
    setHasError(false);
    setErrorMessage('');
    //it was sending two requests for send two verification requests
    submitMethod && e.key === 'Enter' && (submitMethod as () => void)();
  }

  const handleVerified = (res: any) => {
    salla.log('verified', res);
    const verifyCase = res.data.case || res.error.case;

    if (!verifyCase) {
      salla.log('verified but without case!');
      return;
    }
    if (verifyCase === "new_customer") {
      setCustomFields(res.data.custom_fields || []);
      return registrationTab.current && showTab(registrationTab.current);
    }

    // Clear pending verification data on successful verification
    const storeId = salla.config.get('store.id') || 'unknown';
    clearAuthData(storeId, salla.logger);

    if (res.data.redirect_url) {
      window.location.href = res.data.redirect_url;
      return window.location.href
    }

    parentMessage(res.data, (supportWebAuth && !withoutReload) ? "authenticated_web_auth" : "authenticated");
  }

  function validateField(field: HTMLInputElement, errorMsg: string) {
    if (!field) {
      return
    }
    field.classList.add('s-has-error');
    const nextSibling = field.nextElementSibling;
    if (nextSibling) {
      (nextSibling as HTMLElement).innerText = `* ${errorMsg}`;
    }
  }
  async function newUserValidation() {
    const isLogByPhone = regType === "phone";
    const isLogByEmail = regType === "email";
    const isSocialLogin = regType === "social";
    const emailValue = (regEmail.current?.value) || (isLogByEmail && loginEmail.current && loginEmail.current.value);
    const isEmailValid = emailValue && isValidEmail(emailValue);
    const isFirstNameValid = firstName.current && firstName.current.value.trim().length > 0;
    const isLastNameValid = lastName.current && lastName.current.value.trim().length > 0;
    const isPhoneValid = (regTelInput.current && await regTelInput.current.isValid()) || (loginTelInput.current && isLogByPhone && await loginTelInput.current.isValid());
    const emailValidation = isSocialLogin || (emailValue && isEmailValid) || (!emailValue && !isEmailRequired);

    // Custom Fields Validation
    let isCustomFieldValid = true;
    //we should make sure that there is text nodes between children
    customFieldsWrapperRef.current && (customFieldsWrapperRef.current as HTMLElement)?.childNodes
      .forEach((field: any | HTMLInputElement) => {
        setCustomFieldsValues(prevState => ({
          ...prevState,
          [field.id]: field.value
        }));
        if (!field.required || customFieldsValues[field.id].length) {
          return;
        }
        isCustomFieldValid = false;
        const errorMsg: string = salla.lang.get('common.errors.field_required', { attribute: field.title });
        validateField(field, errorMsg)
      });

    if (emailValidation && isPhoneValid && isFirstNameValid && isLastNameValid && isCustomFieldValid) return;
    !isEmailValid && regEmail.current && !isSocialLogin && validateField(regEmail.current, emailErrorMsg);
    !isFirstNameValid && firstName.current && validateField(firstName.current, firstNameErrorMsg);
    !isLastNameValid && lastName.current && validateField(lastName.current, lastNameErrorMsg);
    throw new Error('Please insert required fields');
  }


  const newUser = async () => {
    let regPhone = '';
    let countryCode = '';

    if ((regType === "email" || regType === "social") && regTelInput.current) {
      const values = await regTelInput.current.getValues();
      if (values && 'phone' in values && 'countryCode' in values) {
        regPhone = values.phone;
        countryCode = values.countryCode;
      }
    } else if (loginTelInput.current) {
      const values = await loginTelInput.current.getValues();
      if (values && 'phone' in values && 'countryCode' in values) {
        regPhone = values.phone;
        countryCode = values.countryCode;
      }
    }

    const emailValue = (regEmail.current?.value) || (loginEmail.current?.value);

    await newUserValidation();
    setIsLoading(true);

    let data = {
      first_name: firstName.current?.value,
      last_name: lastName.current?.value,
      phone: regPhone || (loginTelInput.current?.getValues().then((values) => values.phone)),
      country_code: countryCode,
      verified_by: regType,
      custom_fields: customFieldsValues,
      email: '',
      marketing_opt_in: agreeToMarketing
    };

    if (emailValue) {
      data = { ...data, email: emailValue }
    }

    if (regType === 'social') {
      data = { 
        ...data, 
        ...(socialData.provider_id && { provider_id: socialData.provider_id }),
        provider: socialData.provider,
        social_registration_token: socialData?.social_registration_token
      } as any;
    }

    const code = verifyRef.current?.getCode();
    salla.auth.api
      .register({ ...data, code })
      .then((res: any) => {
        parentMessage({ ...res.data, verification: { type: regType } }, (supportWebAuth && !withoutReload) ? "authenticated_web_auth" : "authenticated");
      })
      .then(() => setIsLoading(false))
      .catch((error: Error) => {
        salla.logger.error(error);
        setIsLoading(false);
      });
  }



  async function loginBySMS(event: SyntheticEvent | null = null): Promise<void> {
    event?.preventDefault();
    setIsLoading(true);
    try {
      await waitForCondition(() => !!window.intlTelInputUtils);
      if(Salla.config.get('store.features', []).includes('re-captcha-v3') && !await waitForTurnstile()){
        setIsLoading(false);
        setHasError(true);
        setErrorMessage('Failed to validate! please try again.');
        return;
      }

      const values = loginTelInput.current ? await loginTelInput.current.getValues() : {};
      const { phone = '', countryCode = '', countryKey = '' } = values;
      const isPhoneValid = loginTelInput.current && await loginTelInput.current.isValid();

      if (!isPhoneValid || !phone) {
        setIsLoading(false);
        setHasError(true);
        return;
      }

      if (!phone || !countryCode) {
        salla.logger.error('Login Error: Missing phone or country code');
        Salla.analytics.log('Login Error::loginBySMS', {
          error_type: 'missing_data',
          has_phone: !!phone,
          has_country_code: !!countryCode
        });
        setIsLoading(false);
        setHasError(true);
        setErrorMessage('Failed to retrieve phone data. Please try again.');
        return;
      }

      const loginData = { type: 'mobile' as const, phone, country_code: countryCode, country_key: countryKey };
      salla.logger.log('loginBySMS: Prepared data', loginData);
      smsBtn.current && await login(smsBtn.current, loginData);
    } catch (error) {
      setHasError(true);
      setErrorMessage(errorMsg);
      console.error(error);
      parentMessage({ error: error instanceof Error ? error.message : 'Failed to login via SMS' }, 'sms-login-error');
    } finally {
      setIsLoading(false);
    }
  }

  function isValidEmail(email: string) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }


  const loginByEmail = (event: React.FormEvent<HTMLFormElement> | null = null) => {
    event?.preventDefault();
    if (loginEmail.current && !isValidEmail(loginEmail.current.value)) {
      setHasError(true);
      setErrorMessage(`* ${emailErrorMsg}`);
      return;
    }
    if (loginEmail.current && emailBtn.current) {
      login(emailBtn.current, { type: 'email', email: loginEmail.current.value });
    }
  };


  const login = (_btn: HTMLElement, data: any) => {
    const isValidData = data && data.type && (data.email || (data.phone && data.country_code));
    if (!isValidData) {
      salla.logger.warn('Login Warning: Data structure validation failed', data);
      Salla.analytics.log('Login Warning::login', { error_type: 'pre_validation_check', data: JSON.stringify(data) });
    }

    const loginData = typeof structuredClone !== 'undefined'
      ? structuredClone(data)
      : JSON.parse(JSON.stringify(data));
    salla.logger.log('login: Starting with data type', data.type);

    // Track OTP Requested
    Salla.analytics.track('OTP Requested', {
      type: data.type,
      method: data.type === 'email' ? 'email' : 'mobile',
      ...(data.type === 'email' ? { email: data.email } : { phone: data.phone, country_code: data.country_code })
    });

    const storeId = salla.config.get('store.id') || 'unknown';
    saveAuthData(loginData, storeId, salla.logger);

    setIsLoading(true);
    setHasError(false);
    setErrorMessage('');
    return salla.auth.api.login(loginData)
      .then(() => {
        salla.logger.log('login: API call successful, opening verify');
        // Track OTP Request Sent
        Salla.analytics.track('OTP Request Sent', {
          type: data.type,
          method: data.type === 'email' ? 'email' : 'mobile'
        });
        setIsLoading(false);
        verifyTab.current && showTab(verifyTab.current as unknown as HTMLElement)
      })
      .then(() => {
        setShowVerify(true)
        // Use requestAnimationFrame + setTimeout to ensure the Verify component is fully mounted
        // and the ref is available before calling open()
        const tryOpenVerify = (attempts = 0) => {
          if (verifyRef.current) {
            salla.logger.log('login: Opening verify (attempt ' + attempts + ')');
            verifyRef.current.open(loginData);
          } else if (attempts < 10) {
            // Retry with exponential backoff up to 10 attempts
            const delay = Math.min(50 * Math.pow(1.5, attempts), 500);
            salla.logger.log('login: verifyRef not ready, retrying in ' + delay + 'ms');
            setTimeout(() => tryOpenVerify(attempts + 1), delay);
          } else {
            salla.logger.error('Login Error: verifyRef.current is null after multiple attempts');
            Salla.analytics.log('Login Error::verifyRef', {
              error_type: 'ref_null_after_retries',
              attempts: attempts
            });
          }
        };
        // Start with requestAnimationFrame to ensure render is complete
        requestAnimationFrame(() => tryOpenVerify(0));
      })
      .catch((error: any) => {
        // Track OTP Request Failed
        Salla.analytics.track('OTP Request Failed', {
          type: data.type,
          method: data.type === 'email' ? 'email' : 'mobile',
          error: error.response?.data?.error?.message || error.message || 'Unknown error'
        });
        setHasError(true);
        setErrorMessage(errorMsg);
        setIsLoading(false);
        // Clear the pending verification data on error
        const storeId = salla.config.get('store.id') || 'unknown';
        clearAuthData(storeId, salla.logger);
      })
      .finally(() => setIsLoading(false));
  };

  const getFilepondPlaceholder = () => {
    return `<div class="s-login-modal-filepond-placeholder"><span class="s-login-modal-filepond-placeholder-icon"><svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
    <title>camera</title>
    <path d="M16 10.667c-4.044 0-7.333 3.289-7.333 7.333s3.289 7.333 7.333 7.333 7.333-3.289 7.333-7.333-3.289-7.333-7.333-7.333zM16 22.667c-2.573 0-4.667-2.093-4.667-4.667s2.093-4.667 4.667-4.667 4.667 2.093 4.667 4.667-2.093 4.667-4.667 4.667zM28 5.333h-3.287l-2.271-3.407c-0.248-0.371-0.664-0.593-1.109-0.593h-10.667c-0.445 0-0.861 0.223-1.109 0.593l-2.271 3.407h-3.287c-2.205 0-4 1.795-4 4v17.333c0 2.205 1.795 4 4 4h24c2.205 0 4-1.795 4-4v-17.333c0-2.205-1.795-4-4-4zM29.333 26.667c0 0.735-0.599 1.333-1.333 1.333h-24c-0.735 0-1.333-0.599-1.333-1.333v-17.333c0-0.735 0.599-1.333 1.333-1.333h4c0.445 0 0.861-0.223 1.109-0.593l2.272-3.407h9.239l2.271 3.407c0.248 0.371 0.664 0.593 1.109 0.593h4c0.735 0 1.333 0.599 1.333 1.333zM25.333 10.66c-0.736 0-1.333 0.604-1.333 1.34s0.597 1.333 1.333 1.333 1.333-0.597 1.333-1.333v-0.013c0-0.736-0.597-1.327-1.333-1.327z"></path>
    </svg></span><p class="s-login-modal-filepond-placeholder-text">${salla.lang.get('common.uploader.drag_and_drop')}</p> <span class="filepond--label-action">${salla.lang.get('common.uploader.browse')}</span></div>`
  }

  function showTab(tab: HTMLElement, evt?: React.MouseEvent) {
    evt?.preventDefault();
    setHasError(false);
    setErrorMessage('');
    setCurrentTabName((tab as HTMLInputElement).getAttribute('data-name') || '');

    if (!tab) {
      return;
    }
    if (tab === mobileTab.current) {
      setRegType('phone');
    } else if (tab === emailTab.current) {
      setRegType('email');
    }

    if (tab === registrationTab.current) {
      return;
    }

    handleAutoFocusOnFirstLoad(tab);
  }


  function getSelectedTab() {
    if (isMobileAllowed) {
      mobileTab.current && showTab(mobileTab.current);
      setRegType('phone');
    } else if (isEmailAllowed) {
      emailTab.current && showTab(emailTab.current);
      setRegType('email');
    }
  }

  function handleAutoFocusOnFirstLoad(tab?: HTMLElement) {
    if (inline) return;
    const container = tab || document;
    const input = container.querySelector(
      'input[type="tel"], input[type="email"]'
    ) as HTMLInputElement | null;
    input &&
      setTimeout(() => {
        input.focus();
      }, 100);
  }

  function handleBackButton() {
    // Clear pending verification data when user goes back
    const storeId = salla.config.get('store.id');
    clearAuthData(storeId, salla.logger);

    if (mobileTab.current && regType === 'phone') {
      showTab(mobileTab.current);
    } else if (emailTab.current && regType === 'email') {
      showTab(emailTab.current);
    }
  }

  const handleSocialLogin = (provider: SocialProviderConfig) => {
    const isAppWebview = Salla?.config.get('store.features', []).includes('app-webview-login');

    if (isAppWebview) {
      Salla.event.emit('auth::social-media-login', { provider: provider.id, url: provider.loginUrl });
      return;
    }

    const popup = window.open(provider.loginUrl, `social-login-${provider.id}`, 'width=500,height=600,top=100,left=100');

    if (!popup || popup.closed) {
      salla.notify.error(salla.lang.get('common.errors.error_occurred'));
      return;
    }
    setIsLoading(true);
  };

  useEffect(() => {
    getSelectedTab();
    handleAutoFocusOnFirstLoad();

    const handleMessage = (event: MessageEvent) => {
      const payload = event.data;
      if (!payload || typeof payload !== 'object') return;

      const messageType = payload.type;
      const isSocialError = socialErrorTypes.includes(messageType) || payload.error;
      const isSocialSuccess = socialSuccessTypes.includes(messageType);

      if (!isSocialError && !isSocialSuccess) return;

      if (isSocialError) {
        setIsLoading(false);
        setHasError(true);
        const err = payload.error;
        const msg = typeof err === 'string' ? err : err?.message ?? (err != null ? JSON.stringify(err) : errorMsg);
        setErrorMessage(msg);
        salla.notify.error(msg);
        return;
      }

      if (isSocialSuccess) {
        const providerConfig = enabledSocialProviders.find((p) => p.id === payload.provider);
        const params = {
          code_verifier: payload?.code_verifier ?? null,
          code: payload.code,
          redirect_uri: providerConfig?.callbackUrl,
          user: payload?.user ?? null,
        };

        salla.api.request(`auth/social-callback/${payload.provider}`, params, 'post')
          .then((response: any) => {
            console.log('response', response)
            const customerData = response.data;
            const caseType = customerData?.case;

            if (caseType === 'new_customer') {
              setIsLoading(false);
              setCustomFields(customerData.custom_fields ?? []);
              const customer = customerData.customer;
            
              if (customer.first_name != null && firstName.current) firstName.current.value = String(customer.first_name);
              if (customer.last_name != null && lastName.current) lastName.current.value = String(customer.last_name);
              if (customer.email != null && regEmail.current) regEmail.current.value = String(customer.email);

              // Save social data for registration
              setSocialData({
                provider_id: customer.provider_id,
                provider: payload.provider,
                social_registration_token: customerData?.social_registration_token ?? null
              });

              setRegType(customerData.verification?.type ?? 'social');
              
              registrationTab.current && showTab(registrationTab.current);
            } else if (customerData?.redirect_url) {
              window.location.href = customerData.redirect_url;
            } else {
              parentMessage(
                customerData,
                (supportWebAuth && !withoutReload) ? "authenticated_web_auth" : "authenticated"
              );
            }
        })
        .catch((error: any) => {
          setHasError(true);
          setIsLoading(false);
          console.error(error);
          setErrorMessage(errorMsg);
          parentMessage({ error: error?.message || 'Social login failed' }, 'social-login-error');
        });
        
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line
  }, []);

  function handleGuestCheckout() {
    Salla.analytics.track('Guest Login');
    parentMessage({}, 'guest-checkout');
  }

  return (
    <div className={`s-login-modal-wrapper `}>
      {!hideHeader && (
        <div className="s-login-modal-header-inner">
          <span slot="icon" className="s-login-modal-header-icon">
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
              <path d="M16 16c4.412 0 8-3.588 8-8s-3.588-8-8-8-8 3.588-8 8 3.588 8 8 8zM16 2.667c2.941 0 5.333 2.392 5.333 5.333s-2.392 5.333-5.333 5.333-5.333-2.392-5.333-5.333 2.392-5.333 5.333-5.333zM16 18.667c-7.476 0-13.333 3.66-13.333 8.333v3.667c0 0.736 0.597 1.333 1.333 1.333s1.333-0.597 1.333-1.333v-3.667c0-3.019 4.984-5.667 10.667-5.667s10.667 2.648 10.667 5.667v3.667c0 0.736 0.597 1.333 1.333 1.333s1.333-0.597 1.333-1.333v-3.667c0-4.673-5.857-8.333-13.333-8.333z" />
            </svg>
          </span>
          <div className="s-login-modal-header-content">
            <div className="s-login-modal-title">{salla.lang.get('blocks.header.login')}</div>
          </div>
        </div>
      )}
      {/* Tab 2 (Login By Mobile)*/}
      {isMobileAllowed ?
        <div className={generateTabClasses('login-phone')} data-name="login-phone"
          ref={mobileTab}>
          <form onSubmit={(event) => loginBySMS(event)} className={hasError && errorMessage ? 'has-error' : ''} method="POST">
            <label className="s-login-modal-label">{salla.lang.get('common.elements.mobile')}</label>
            <TelInput
              onKeyDown={e => typing(e)}
              phoneEntered={(phone) => setPhone(phone)}
              ref={loginTelInput}
              name="phone"
              salla={salla}
            />

            <TurnstileWidget/>

            {hasError && errorMessage ? <span className="s-login-modal-error-message">
              {errorMessage}
            </span> : ''}
            {/* @ts-ignore */}
            <button className="mt-3 btn btn--primary" type="submit" disabled={isLoading} ref={smsBtn}>
              {isLoading ? <Loading size="sm" /> : salla.lang.get('blocks.header.enter')}
            </button>
          </form>
          {isEmailAllowed ?
            <button type="button" onClick={() => emailTab.current && showTab(emailTab.current)} className={`s-login-modal-link ${outlineButtonNoBorder? 's-login-outline-no-border' : 'link-outline'}`}>{salla.lang.get('blocks.header.login_by_email')}</button> : ''
          }
          
          {enabledSocialProviders.length > 0 && (
            <>
              <div className="s-login-modal-social-separator">
                <span className="s-login-modal-social-separator-text">
                  {salla.lang.get('common.elements.or_login_with')}
                </span>
              </div>
              <div className="s-login-modal-social-buttons">
                {enabledSocialProviders.map((provider) => (
                  <button
                    key={provider.id}
                    type="button"
                    className="s-login-modal-social-btn"
                    onClick={() => handleSocialLogin(provider)}
                    aria-label={`Login with ${provider.label}`}
                  >
                    {provider.icon}
                  </button>
                ))}
              </div>
            </>
          )}

          <slot name="after-login-mobile" />
        </div> : ''}
      {isEmailAllowed ?
        <div className={generateTabClasses('login-email')} data-name="login-email"
          ref={emailTab}>
          <label className="s-login-modal-label">{salla.lang.get('common.elements.email')}</label>
          <form onSubmit={(event) => loginByEmail(event)} method="POST">
            <input type="email" ref={loginEmail} onKeyDown={e => typing(e)}
              placeholder="your@email.com"
              enterKeyHint="next"
              className={`s-login-modal-input s-ltr ${hasError ? 's-has-error' : ''}`} />

            {hasError && errorMessage ? <span className="s-login-modal-error-message">
              {errorMessage}
            </span> : ''}
            <button className="mt-3 btn btn--primary" disabled={isLoading} type="submit" ref={emailBtn}>
              {isLoading ? <Loading size="sm" /> : salla.lang.get('blocks.header.enter')}
            </button>
          </form>
          {isMobileAllowed ?
            <button type='button' onClick={() => mobileTab.current && showTab(mobileTab.current)}
              className={`s-login-modal-link ${outlineButtonNoBorder ? 's-login-outline-no-border' : 'link-outline'}`}>{salla.lang.get('blocks.header.login_by_sms')}</button>
            : ''
          }
          
          {enabledSocialProviders.length > 0 && (
            <>
              <div className="s-login-modal-social-separator">
                <span className="s-login-modal-social-separator-text">
                  {salla.lang.get('common.elements.or_login_with')}
                </span>
              </div>
              <div className="s-login-modal-social-buttons">
                {enabledSocialProviders.map((provider) => (
                  <button
                    key={provider.id}
                    type="button"
                    className="s-login-modal-social-btn"
                    onClick={() => handleSocialLogin(provider)}
                    aria-label={`Login with ${provider.label}`}
                  >
                    {provider.icon}
                  </button>
                ))}
              </div>
            </>
          )}
        </div> : ''}
      {/* Tab 4 (Verify OTP)*/}
      <div ref={verifyTab} className={generateTabClasses('otp')} data-name="otp">
        {showVerify && [<Verify key="verify" salla={salla} ref={verifyRef} supportWebAuth={supportWebAuth} verified={(res) => handleVerified(res)} />,
        <button key="phone-tab" type="button" onClick={() => handleBackButton()}
          className="s-login-modal-verify-back"
        >
          <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
            <path d="M29.217 15.465c-0.019-0.044-0.056-0.077-0.080-0.119-0.067-0.116-0.139-0.227-0.236-0.317-0.011-0.009-0.013-0.024-0.024-0.033l-10.667-9.333c-0.553-0.484-1.396-0.429-1.881 0.125-0.484 0.555-0.428 1.396 0.127 1.881l7.996 6.997h-20.452c-0.737 0-1.333 0.597-1.333 1.333s0.596 1.333 1.333 1.333h20.452l-7.996 6.997c-0.555 0.485-0.611 1.327-0.127 1.881 0.264 0.3 0.633 0.455 1.004 0.455 0.312 0 0.625-0.109 0.877-0.331l10.667-9.333c0.011-0.009 0.015-0.024 0.024-0.033 0.097-0.091 0.169-0.201 0.236-0.317 0.024-0.041 0.060-0.075 0.080-0.119 0.073-0.163 0.116-0.343 0.116-0.533s-0.043-0.371-0.116-0.535z" />
          </svg>
        </button>]}
      </div>
      <div ref={registrationTab} data-name="registration" className={generateTabClasses('registration')}>
        <div>
          <label className="s-login-modal-label">{salla.lang.get('blocks.header.your_name')}</label>
          <input
            type="text"
            className="s-login-modal-input"
            ref={firstName}
            onKeyDown={(e) => typing(e, newUser)}
            placeholder={salla.lang.get('pages.profile.first_name')}
          />
          <span className="s-login-modal-error-message" />
        </div>
        <div>
          <label className="s-login-modal-label">{salla.lang.get('pages.profile.last_name')}</label>
          <input
            type="text"
            className="s-login-modal-input"
            ref={lastName}
            onKeyDown={(e) => typing(e, newUser)}
            placeholder={salla.lang.get('pages.profile.last_name')}
          />
          <span className="s-login-modal-error-message" />
        </div>
        <div className={generateRegClasses('phone') + ' relative'}>
          <label className="s-login-modal-label">{salla.lang.get('common.elements.mobile')}</label>
          <TelInput
            onKeyDown={e => typing(e, newUser)}
            phoneEntered={(phone) => setPhone(phone)}
            ref={regTelInput}
            name="phone"
            salla={salla}
            isRegistration
          />
        </div>
        <div className={generateRegClasses('email') + (regType === 'social' ? ' s-hidden' : '')}>
          <label className="s-login-modal-label">{salla.lang.get('common.elements.email')}</label>
          <input
            type="email"
            ref={regEmail}
            onKeyDown={(e) => typing(e, newUser)}
            placeholder="your@email.com"
            className="s-login-modal-input s-ltr"
          />
          <span className="s-login-modal-error-message" />
        </div>
        <div className="s-login-modal-custom-fields" ref={customFieldsWrapperRef}>
          {customFields.map((field: CustomField, i) => (
            <React.Fragment key={i}>
              <label className="s-login-modal-label">{field.label}</label>
              {field.type === CustomFieldType.PHOTO ? (
                <FileUploader
                  fileTypes={['image/png', 'image/jpeg', 'image/jpg', 'image/gif']}
                  onUploadComplete={(value) => { console.log(value) }}
                  url={salla.url.get('upload-image')}
                  labelIdle={getFilepondPlaceholder()}
                  name='image'
                  salla={salla}
                />

              ) : (
                <input
                  onInput={(el) => (field.type === CustomFieldType.NUMBER ? salla.helpers.inputDigitsOnly(el.target) : {})}
                  onKeyDown={(e) => typing(e, newUser)}
                  className="s-login-modal-input s-ltr"
                  maxLength={field.length || 1000}
                  placeholder={field.description}
                  required={field.required}
                  title={field.label}
                  id={`${field.id}`}
                  type="text"
                />
              )}
              <span className="s-login-modal-error-message" />
            </React.Fragment>
          ))}
        </div>

        {/* Agree to marketing */}
        <label className="flex flex-row items-center gap-1.5 my-4 cursor-pointer">
          <input
            name="agree_to_marketing"
            type="checkbox"
            className="s-login-modal-checkbox"
            checked={agreeToMarketing}
            onChange={(e) => setAgreeToMarketing(e.target.checked)}
          />
          <span>{salla.lang.get('common.elements.agree_to_marketing')}</span>
        </label>

        {/* Register button */}
        <button
          type="button"
          className="mt-3 btn btn--primary"
          disabled={isLoading}
          onClick={() => newUser()}
          ref={regBtn}
        >
          {isLoading ? <Loading size="sm" /> : salla.lang.get('blocks.header.register')}
        </button>

        {/* Terms of service and privacy policy */}
        <p className="s-login-modal-footer-text mt-2 text-sm opacity-80">
          {salla.lang.get('common.elements.agree_to_terms_on_register')}
          {' '}<a className='text-primary' href={urls.privacyPolicy} target="_blank" rel="noopener noreferrer">{salla.lang.get('common.elements.privacy_policy')}</a>
        </p>
      </div>
      {
        showGuestCheckoutLink() && <button id="guest-checkout" className="s-login-modal-link" onClick={() => handleGuestCheckout()}>
          {salla.lang.get('blocks.header.continue_as_guest')}
        </button>
      }

    </div>
  )
};

export default Login;
export type { LoginProps };
